import { useCallback, useState } from "react";
// Relative: a pure hook, resolvable under Jest without a shim.
import useAdminLoad from "../hooks/useAdminLoad";

/**
 * The one read shape every pathway read hook shares: `{ data, loading, error, notFound, reload }`.
 *
 * FOUR OUTCOMES, NOT THREE. `useAdminLoad` already separates "loaded" from "could not
 * load" (BUG-051); a member URL has a fourth answer, "there is no such pathway for you"
 * (HTTP 404 — the contract answers it for absent, draft AND not-a-member alike, so the
 * existence of a pathway the reader is not enrolled in is never disclosed). It is `notFound`, never `error`:
 * a retry button on a pathway that will never be yours is a dead end dressed as a control.
 * Every other failure — 401, 5xx, a shape refusal — stays `error`.
 *
 * `notFound` is true EXACTLY on HTTP 404 and is reset at the start of every attempt, so a
 * reload that succeeds stops reporting it.
 *
 * @param {() => Promise<any>} fetcher STABLE (`useCallback`) async reader; may throw.
 */
export default function useMemberRead(fetcher) {
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setNotFound(false);
    let next;
    try {
      next = await fetcher();
    } catch (e) {
      if (e?.response?.status === 404) {
        setNotFound(true);
        setData(null);
        return;
      }
      throw e;
    }
    setData(next);
  }, [fetcher]);

  const { loading, loadError, reload } = useAdminLoad(load);

  return { data, loading, error: loadError, notFound, reload };
}
